//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

// Imports
import { DefaultTheme, css } from 'styled-components';

import {
  AudienceTargetingSections,
  BoxType,
  Gender,
  PackageInsertType,
  PackageWeightType,
  Platform,
  RedemptionType,
  RiskManagement,
  SendTo,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import { DimensionFormat } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import brandedBox from '@images/campaigns/sampling/branded-box.png';
import plainBox from '@images/campaigns/sampling/plain-box.png';

// Generic
// Constants and utilities used across the page

export const BV_SAMPLING_PROGRAM = 'bvsampling';

export const USER_ERROR_MESSAGES = {
  DEFAULT: 'Please try again or contact Bazaarvoice Support.',
  NO_SAMPLING_ACCESS:
    "You don't have Sampling access set up for your account. Contact Bazaarvoice Support to get access.",
};

// Generic column width settings
export const WIDTH_ACTIONS = ({ theme }: { theme: DefaultTheme }) => css`
  width: calc(${theme.spacings[3]} * 4);
`;

export const WIDTH_NUMBER = ({ theme }: { theme: DefaultTheme }) => css`
  width: calc(${theme.spacings[3]} * 6);
`;

// Products
// Constants and configurations specific to the products section

export enum EditableProductsSortKey {
  NAME = 'name',
  QUANTITY = 'quantity',
  STATUS = 'isMissingDetails',
  SOURCE = 'newProduct',
}

export const NO_PDP_URL = 'No product page URL supplied for this product.';

export const WIDTH_DIMENSION = ({ theme }: { theme: DefaultTheme }) => css`
  width: calc(${theme.spacings[3]} * 5);
`;

export const WIDTH_IMAGE = css`
  width: 100px;
`;

export const WIDTH_STATUS = ({ theme }: { theme: DefaultTheme }) => css`
  width: calc(${theme.spacings[3]} * 6);
`;

export const CATALOG_PRODUCT_IMAGE_SIZE = ({
  theme,
}: {
  theme: DefaultTheme;
}) => css`
  width: calc(${theme.spacings[1]} * 18);
  height: calc(${theme.spacings[1]} * 18);
`;

// Audience
// Constants and configurations specific to the audience section

export const GENDER_OPTIONS: { label: string; value: Gender }[] = [
  { label: 'Male', value: 'male' },
  { label: 'Female', value: 'female' },
  { label: 'Non-binary', value: 'non-binary' },
];

export const DEFAULT_MIN_ANY_AGE = 1;
export const DEFAULT_MAX_ANY_AGE = 100;
export const DEFAULT_MIN_RANGE_AGE = 18;
export const DEFAULT_MAX_RANGE_AGE = 65;

export const AUDIENCE_AGE_OPTIONS = [
  {
    value: 'any',
    label: 'All ages',
    minAge: DEFAULT_MIN_ANY_AGE,
    maxAge: DEFAULT_MAX_ANY_AGE,
  },
  {
    value: 'range',
    label: 'Range',
    minAge: DEFAULT_MIN_RANGE_AGE,
    maxAge: DEFAULT_MAX_RANGE_AGE,
  },
];

export const EXPANDED_SECTIONS_MAPPING: {
  [key in keyof AudienceTargetingSections]: {
    title: string;
    description: string;
  };
} = {
  location: {
    title: 'Where do your desired participants live?',
    description:
      'Enter information you want participants to provide about their homes to participate in this campaign. For example, zip code, real estate type, building floor, proximity to retail outlets to collect items in store, outdoor space, and climate.',
  },
  pets: {
    title: 'Is this product for pets?',
    description:
      'Enter information you want participants to provide about their pets to participate in this campaign. For example, pet type, size, weight, age, and any specific needs.',
  },
  children: {
    title: 'Is this product for children?',
    description:
      'Enter information about the target audience for the product. For example, age range or gender.',
  },
  household: {
    title:
      'Do your desired participants homes need to have any specific features?',
    description:
      'Enter information you want participants to provide about their home features to participate in this campaign. For example, "Bathtub", "Lawn".',
  },
  installation: {
    title: 'Does this product require installation?',
    description:
      'Enter information you want participants to provide about installation requirements to participate in this campaign. For example, "Requires professional installation", "DIY installation".',
  },
  exclude: {
    title:
      'Which participants should be excluded, and what information should we ask participants to exclude?',
    description:
      'Enter information to help us exclude participants or add conditions for member reviews. For example, specify whether the product can be used by pregnant women or that participants should not mention competitor brands in their review.',
  },
  extraRequirements: {
    title: 'Any other requirements?',
    description:
      'Enter any additional information you want participants to provide to participate in this campaign.',
  },
};

// Tasks
// Constants and configurations specific to the tasks section

export const INFLUENSTER_HANDLE = '@influenster';

export const DEFAULT_HASHTAGS = '#complimentary';

export const MAX_INSTAGRAM_HASHTAGS = 30;

export const MAX_HANDLES = 20;

export const INSTAGRAM_OPTIONS: { label: string; value: Platform }[] = [
  {
    label: 'Story',
    value: 'instagramStory',
  },
  {
    label: 'Post',
    value: 'instagramPost',
  },
];

export const TIKTOK_OPTION: { label: string; value: Platform } = {
  label: 'Video',
  value: 'tiktok',
};

export const MAX_TIKTOK_HASHTAGS = 5;

// Fulfillment
// Constants and configurations specific to the fulfillment section

export const MAX_CATALOG_UPLOAD_SIZE_MB = 5 * 1024 * 1024;

export const BRANDED_BOX_DESIGN_GUIDELINES =
  'https://docs.google.com/presentation/d/1Tak7-x5H1SaOl2GoNech7v_vje40vQtm';

export const DIMENSION_FORMATS: Record<DimensionFormat, { label: string }> = {
  IN: { label: 'Inches (IN")' },
  CM: { label: 'Centimeters (CM)' },
};

export const getBoxOptions = (
  bvClientId?: string,
): Record<BoxType, { label: string; image: string }> => ({
  plain: {
    label: 'Plain (standard)',
    image: plainBox,
  },
  branded: {
    label: 'Branded (custom)',
    image: brandedBox,
  },
  'client-provided': {
    label: `Provided by you${bvClientId ? ` (${bvClientId})` : ''}`,
    image: brandedBox,
  },
});

export const PACKAGE_WEIGHT_OPTIONS: Record<
  PackageWeightType,
  { label: string }
> = {
  imperial: {
    label: 'Pounds (lbs)',
  },
  metric: {
    label: 'Kilograms (kgs)',
  },
};

export const getPackageInsertOptions = (
  bvClientId?: string,
): Record<PackageInsertType, { label: string; instructions?: string }> => ({
  none: {
    label: 'No inserts',
  },
  'client-provided': {
    label: 'Client-Provided Inserts',
    instructions: `You${
      bvClientId ? ` (${bvClientId})` : ''
    } will print and provide the physical inserts to be included.`,
  },
});

export const WAREHOUSE_NOTIFICATIONS_OPTIONS: {
  value: SendTo;
  label: string;
}[] = [
  {
    value: 'sendToMe',
    label: 'Send to request owner',
  },
  {
    value: 'sendToOther',
    label: 'Send to someone else',
  },
];

export const RISK_MANAGEMENT_RECOMMENDATION_TEXTS = {
  BV_SHIPPED: {
    ACCEPTING:
      'I will send an extra 3% of each product to the warehouse in case of shipping issues. I understand that any unused products will be destroyed or donated.',
    DECLINING:
      'I am declining the recommendation to send an extra 3% of products to the warehouse. I understand that, if products become damaged or go missing in transit, this could result in lower volumes of user-generated content.',
  },
  CLIENT_SHIPPED: {
    ACCEPTING:
      'Please contact me if a significant portion of my products are affected by shipping issues and I will reship to these members.',
    DECLINING:
      'If products become damaged or go missing in transit, I will not reship to members so please do not contact me. I understand that this could result in lower volumes of user-generated content.',
  },
};

export const getRiskManagementOptions = (isClientShipped: boolean) => [
  {
    value: 'acceptingRecommendation' as RiskManagement,
    label:
      RISK_MANAGEMENT_RECOMMENDATION_TEXTS[
        isClientShipped ? 'CLIENT_SHIPPED' : 'BV_SHIPPED'
      ].ACCEPTING,
  },
  {
    value: 'decliningRecommendation' as RiskManagement,
    label:
      RISK_MANAGEMENT_RECOMMENDATION_TEXTS[
        isClientShipped ? 'CLIENT_SHIPPED' : 'BV_SHIPPED'
      ].DECLINING,
  },
];

export const REDEMPTION_TYPE_MAPPING: Record<RedemptionType, string> = {
  instore: 'In Store',
  online: 'Online',
  instorePickup: 'In Store Pickup',
};

// Purchase Options
// Constants and configurations specific to the purchase options section

export enum PurchaseOptionsSortKey {
  EXPIRY_DATE = 'expiryDate',
  BALANCE = 'balance',
}

// Summary

export enum ViewOnlyProductsSortKey {
  NAME = 'name',
  QUANTITY = 'quantity',
  SOURCE = 'newProduct',
}

// Specific column width settings for the purchase options table

export const WIDTH_CHECKBOX = ({ theme }: { theme: DefaultTheme }) => css`
  width: calc(${theme.spacings[2]} * 7);
`;

export const WIDTH_UNITS = ({ theme }: { theme: DefaultTheme }) => css`
  width: calc(${theme.spacings[2]} * 12);
`;

export const WIDTH_EXPIRATION_DATE = ({
  theme,
}: {
  theme: DefaultTheme;
}) => css`
  width: calc(${theme.spacings[2]} * 18);
`;
